import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import LoginPage from './components/LoginPage';
import JoinPage from './components/JoinPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import ChangePasswordModal from './components/ChangePasswordModal';
import SalesHub from './components/SalesHub';
import AdsHub from './components/AdsHub';
import IntakeForm from './components/IntakeForm';
import SharePage from './components/SharePage';
import PlanBuilder from './components/PlanBuilder';
import PlanProposal from './components/PlanProposal';
import PublicMarketReport from './components/PublicMarketReport';
import OnboardWizard from './components/OnboardWizard';
import PublicBookingPage from './components/PublicBookingPage';
import { Toaster } from './components/ui/sonner';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";
axios.defaults.withCredentials = true;

// Department → hub mapping (Support Hub retired; new "ads" dept lives here)
const ADS_DEPTS  = ["ads"];
const SALES_DEPTS = ["sales", "sales_lead", "sales_rep"];

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [activeHub, setActiveHub] = useState(null); // "sales" | "ads"

  // URL parsing — every public route lives here
  const pathParts = window.location.pathname.split('/');
  const isJoinPage = pathParts[1] === 'join' && pathParts[2];
  const inviteCode = isJoinPage ? pathParts[2] : null;
  const isResetPage = pathParts[1] === 'reset' && pathParts[2];
  const resetToken = isResetPage ? pathParts[2] : null;
  const isPlanPage = pathParts[1] === 'plan' && !pathParts[2];
  const isPlanBuilder = pathParts[1] === 'plan-builder';
  const isPublicReport = pathParts[1] === 'report' && pathParts[2];
  const publicReportShareId = isPublicReport ? pathParts[2] : null;
  const isIntakeForm = pathParts[1] === 'start';
  const isSharePage = pathParts[1] === 'share' && pathParts[2];
  const shareToken = isSharePage ? pathParts[2] : null;
  const isOnboardPage = pathParts[1] === 'onboard';
  const isPublicBookingPage = pathParts[1] === 'onboard' && pathParts[2] === 'book';

  const routeByDept = useCallback((dept) => {
    if (SALES_DEPTS.includes(dept)) setActiveHub("sales");
    else if (ADS_DEPTS.includes(dept)) setActiveHub("ads");
    else setActiveHub(null);
  }, []);

  const checkSession = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      if (res.data && res.data.user_id) {
        setUser(res.data);
        if (res.data.must_change_password) setMustChangePassword(true);
        const dept = res.data.department || "ads";
        if (SALES_DEPTS.includes(dept)) setActiveHub("sales");
        else if (ADS_DEPTS.includes(dept)) setActiveHub("ads");
        else {
          // Admin — restore last hub from localStorage or show picker
          const saved = localStorage.getItem("skyeai_hub");
          setActiveHub(saved === "sales" || saved === "ads" ? saved : null);
        }
      }
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // /plan, /report/{id}, /start, /share/{token}, /onboard are public — skip session check
    if (!isJoinPage && !isResetPage && !isPlanPage && !isPublicReport && !isIntakeForm && !isSharePage && !isOnboardPage) {
      checkSession();
    } else {
      setLoading(false);
    }
  }, [checkSession, isJoinPage, isResetPage, isPlanPage, isPublicReport, isIntakeForm, isSharePage, isOnboardPage]);

  const handleLogin = (userData, sessionToken, mustChange) => {
    setUser(userData);
    if (mustChange) setMustChangePassword(true);
    routeByDept(userData.department || "ads");
  };

  const handleJoined = (userData) => {
    setUser(userData);
    window.history.replaceState({}, '', '/');
    routeByDept(userData.department || "ads");
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(null);
    setActiveHub(null);
    localStorage.removeItem("skyeai_hub");
  };

  const handlePasswordChanged = () => setMustChangePassword(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
        <div className="w-6 h-6 border-2 border-[#9D32F3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Public: /plan renders the dynamic personalized proposal (rep-generated)
  if (isPlanPage) return <PlanProposal />;

  // Public: /start — Ads-Hub client intake form (no auth)
  if (isIntakeForm) return <IntakeForm />;

  // Public: /onboard/book — standalone booking page (no payment gate, no session)
  if (isPublicBookingPage) return <PublicBookingPage />;

  // Public: /onboard — post-sale 3-step wizard (payment → calendar → intake)
  if (isOnboardPage) return <OnboardWizard />;

  // Public: /share/{token} — private client review page (no auth)
  if (isSharePage) return <SharePage token={shareToken} />;

  // Public: shareable Market Domination Report (no auth)
  if (isPublicReport) return <PublicMarketReport shareId={publicReportShareId} />;

  // /plan-builder — INTERNAL, auth required
  if (isPlanBuilder) {
    if (!user) {
      return (
        <>
          <LoginPage onLogin={handleLogin} />
          <Toaster position="top-center" theme="dark" richColors />
        </>
      );
    }
    return <PlanBuilder user={user} onLogout={handleLogout} />;
  }

  // Reset password page — no auth required
  if (isResetPage && resetToken) {
    return (
      <>
        <ResetPasswordPage token={resetToken} onComplete={() => { window.history.replaceState({}, '', '/'); window.location.reload(); }} />
        <Toaster position="top-center" theme="dark" richColors />
      </>
    );
  }

  // Invite page — no auth required
  if (isJoinPage && inviteCode) {
    return (
      <>
        <JoinPage code={inviteCode} onJoined={handleJoined} />
        <Toaster position="top-center" theme="dark" richColors />
      </>
    );
  }

  // Not logged in — show login
  if (!user) {
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <Toaster position="top-center" theme="dark" richColors />
      </>
    );
  }

  const dept = user.department || "ads";
  const isAdmin = dept === "admin";

  const pickHub = (hub) => {
    setActiveHub(hub);
    if (isAdmin) localStorage.setItem("skyeai_hub", hub);
  };

  // Admin hub picker (no hub selected yet)
  if (isAdmin && !activeHub) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
        {mustChangePassword && <ChangePasswordModal onComplete={handlePasswordChanged} />}
        <div className="text-center" data-testid="hub-picker">
          <img src="/skyeai_head.png" alt="Skye AI" className="w-16 h-16 mx-auto mb-6 rounded-2xl" />
          <h1 className="text-2xl font-bold text-white mb-2">Welcome back, {user.name}</h1>
          <p className="text-sm text-[#71717A] mb-8">Choose your workspace</p>
          <div className="flex gap-4">
            <button
              onClick={() => pickHub("sales")}
              data-testid="pick-sales-hub"
              className="group relative w-52 p-6 rounded-2xl border border-[#dc328c]/20 bg-[#dc328c]/[0.04] hover:border-[#dc328c]/50 hover:bg-[#dc328c]/[0.08] transition-all text-left"
            >
              <div className="text-2xl mb-3">&#9670;</div>
              <h3 className="text-lg font-bold text-white mb-1">Sales Hub</h3>
              <p className="text-xs text-[#8A8A93]">Pitch personalizer, proposals, payment links, call debrief</p>
              <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#dc328c] opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
            <button
              onClick={() => pickHub("ads")}
              data-testid="pick-ads-hub"
              className="group relative w-52 p-6 rounded-2xl border border-[#f472b6]/20 bg-[#f472b6]/[0.04] hover:border-[#f472b6]/50 hover:bg-[#f472b6]/[0.08] transition-all text-left"
            >
              <div className="text-2xl mb-3">&#9733;</div>
              <h3 className="text-lg font-bold text-white mb-1">Ads Hub</h3>
              <p className="text-xs text-[#8A8A93]">Client intake, AI creatives, kickoff-call brief, review links</p>
              <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#f472b6] opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
        <Toaster position="top-center" theme="dark" richColors />
      </div>
    );
  }

  // Sales Hub
  if (activeHub === "sales") {
    return (
      <>
        {mustChangePassword && <ChangePasswordModal onComplete={handlePasswordChanged} />}
        <SalesHub
          user={user}
          onLogout={handleLogout}
          onSwitchHub={isAdmin ? () => pickHub("ads") : null}
        />
        <Toaster position="top-center" theme="dark" richColors />
      </>
    );
  }

  // Ads Hub (default for `department: "ads"` and admin fallback)
  return (
    <>
      {mustChangePassword && <ChangePasswordModal onComplete={handlePasswordChanged} />}
      <AdsHub
        user={user}
        onLogout={handleLogout}
        onSwitchHub={isAdmin ? () => pickHub("sales") : null}
      />
      <Toaster position="top-center" theme="dark" richColors />
    </>
  );
}

export default App;
